import type {
  PatchedProviderBoardCertificationWrite,
  PatchedProviderCertificationWrite,
  PatchedProviderImmunizationWrite,
  PatchedProviderLicenseWrite,
  ProviderBoardCertification,
  ProviderBoardCertificationWrite,
  ProviderCertification,
  ProviderCertificationWrite,
  ProviderImmunization,
  ProviderImmunizationWrite,
  ProviderLicense,
  ProviderLicenseWrite,
} from '../../../../../js/api';
import {
  ProviderBoardCertificationsService,
  ProviderCertificationsService,
  ProviderImmunizationsService,
  ProviderLicensesService,
} from '../../../../../js/api';

/**
 * Credential ENTRIES: structured rows (a license has a state, number and status; a
 * board certification a board and eligibility; a life-support certification a type; an
 * immunization a type and an exemption flag) each with ONE backing document in the
 * documents framework (`row.document`). The row is the source of truth for the dates;
 * the backend mirrors them onto the document. The Licenses & Certs tab owns the first
 * three kinds, the Immunizations tab the fourth.
 */

export type CredentialKind = 'license' | 'board' | 'life' | 'immunization';

export type CredentialRow =
  | ProviderLicense
  | ProviderBoardCertification
  | ProviderCertification
  | ProviderImmunization;

export const isLicenseRow = (row: CredentialRow): row is ProviderLicense => 'state' in row;
export const isBoardRow = (row: CredentialRow): row is ProviderBoardCertification =>
  'board_eligible' in row;
export const isImmunizationRow = (row: CredentialRow): row is ProviderImmunization =>
  'exempt' in row;

/**
 * One section of a tab, in display order. A license section is one license TYPE
 * (medical / DEA / CSR / other) and an immunization section one immunization type;
 * board and life-support certifications are one section each. `required`: every
 * clinician is expected to have at least one entry (flags the add bar and the tab
 * label). `documentTypeSlug`: the DocumentType of the backing document (data rows on
 * the `providers.provider` context).
 */
export type CredentialSectionSpec = {
  key: string;
  kind: CredentialKind;
  /** Typed sections (license / immunization): the `type` code the section holds. */
  typeCode?: string;
  documentTypeSlug: string;
  /** Section title for `count` entries (singular for exactly one). */
  title: (count: number) => string;
  /** The add-bar button / dialog title ("Medical License"). */
  label: string;
  required: boolean;
};

const plural = (singular: string, pluralForm: string) => (count: number) =>
  count === 1 ? singular : pluralForm;

export const CREDENTIAL_SECTIONS: readonly CredentialSectionSpec[] = [
  {
    key: 'medical',
    kind: 'license',
    typeCode: 'MEDICAL',
    documentTypeSlug: 'medical-license',
    title: plural('Medical License', 'Medical Licenses'),
    label: 'Medical License',
    required: true,
  },
  {
    key: 'dea',
    kind: 'license',
    typeCode: 'DEA',
    documentTypeSlug: 'dea-license',
    title: plural('DEA License', 'DEA Licenses'),
    label: 'DEA License',
    required: true,
  },
  {
    key: 'csr',
    kind: 'license',
    typeCode: 'CSR',
    documentTypeSlug: 'csr-license',
    title: plural('CSR License', 'CSR Licenses'),
    label: 'CSR License',
    required: false,
  },
  {
    key: 'other',
    kind: 'license',
    typeCode: 'OTHER',
    documentTypeSlug: 'other-license',
    title: plural('Other License', 'Other Licenses'),
    label: 'Other License',
    required: false,
  },
  {
    key: 'board',
    kind: 'board',
    documentTypeSlug: 'board-certification',
    title: plural('Board Certification', 'Board Certifications'),
    label: 'Board Certification',
    required: true,
  },
  {
    key: 'life',
    kind: 'life',
    documentTypeSlug: 'life-support-certification',
    title: plural('Life Support Certification', 'Life Support Certifications'),
    label: 'Life Support Certification',
    required: true,
  },
];

/** ONE DocumentType holds every immunization image; the immunization type is on the row. */
export const IMMUNIZATION_DOCUMENT_TYPE_SLUG = 'immunization';

/** The Immunizations tab: one section per immunization type; TB, flu and COVID-19 are required. */
export const IMMUNIZATION_SECTIONS: readonly CredentialSectionSpec[] = [
  {
    key: 'tb',
    kind: 'immunization',
    typeCode: 'TB',
    documentTypeSlug: IMMUNIZATION_DOCUMENT_TYPE_SLUG,
    title: plural('TB Immunization', 'TB Immunizations'),
    label: 'TB Immunization',
    required: true,
  },
  {
    key: 'flu',
    kind: 'immunization',
    typeCode: 'FLU',
    documentTypeSlug: IMMUNIZATION_DOCUMENT_TYPE_SLUG,
    title: plural('Flu Immunization', 'Flu Immunizations'),
    label: 'Flu Immunization',
    required: true,
  },
  {
    key: 'covid19',
    kind: 'immunization',
    typeCode: 'COVID19',
    documentTypeSlug: IMMUNIZATION_DOCUMENT_TYPE_SLUG,
    title: plural('COVID-19 Immunization', 'COVID-19 Immunizations'),
    label: 'COVID-19 Immunization',
    required: true,
  },
  {
    key: 'other-immunization',
    kind: 'immunization',
    typeCode: 'OTHER',
    documentTypeSlug: IMMUNIZATION_DOCUMENT_TYPE_SLUG,
    title: plural('Other Immunization', 'Other Immunizations'),
    label: 'Other Immunization',
    required: false,
  },
  {
    key: 'record',
    kind: 'immunization',
    typeCode: 'IMMUNIZATIONS',
    documentTypeSlug: IMMUNIZATION_DOCUMENT_TYPE_SLUG,
    title: plural('Immunization Record', 'Immunization Records'),
    label: 'Immunization Record',
    required: false,
  },
];

/** Does `row` belong in `section`? Typed sections match on the row's `type` code. */
export const rowInSection = (row: CredentialRow, section: CredentialSectionSpec): boolean =>
  section.typeCode === undefined || (row as { type?: string }).type === section.typeCode;

export const sectionFor = (
  row: CredentialRow,
  kind: CredentialKind,
  sections: readonly CredentialSectionSpec[] = CREDENTIAL_SECTIONS
): CredentialSectionSpec =>
  sections.find((section) => section.kind === kind && rowInSection(row, section)) ?? sections[0];

export const STAFF_PROVIDER_CREDENTIALS_QUERY_KEY = 'staff-provider-credentials';

export const credentialsQueryKey = (providerId: string, kind: CredentialKind) => [
  STAFF_PROVIDER_CREDENTIALS_QUERY_KEY,
  providerId,
  kind,
];

type Paginated<T> = { results?: T[] } | T[] | undefined;
// a function declaration, not a generic arrow: esbuild parses these files as TSX
function rowsOf<T>(data: Paginated<T>): T[] {
  return Array.isArray(data) ? data : (data?.results ?? []);
}

/**
 * The union of the write shapes, structurally: the generated `type` enums differ per
 * model, so an intersection would collapse to `never`. Each kind's call casts to its
 * own generated type at the boundary.
 */
export type CredentialWrite = {
  provider?: string;
  type?: string;
  state?: string;
  number?: string;
  status?: string;
  board_eligible?: boolean;
  exempt?: boolean;
  issue_date?: string | null;
  expiration_date?: string | null;
  document_id?: string | null;
};

/** The four endpoints behind one shape, keyed by kind. */
export const credentialApi: Record<
  CredentialKind,
  {
    list: (providerId: string) => Promise<CredentialRow[]>;
    create: (body: Partial<CredentialWrite> & { provider: string }) => Promise<CredentialRow>;
    patch: (id: string, body: Partial<CredentialWrite>) => Promise<CredentialRow>;
    destroy: (id: string) => Promise<void>;
  }
> = {
  license: {
    list: (providerId) =>
      ProviderLicensesService.providerLicensesList({ provider: providerId }).then((data) =>
        rowsOf<ProviderLicense>(data as Paginated<ProviderLicense>)
      ),
    create: (body) =>
      ProviderLicensesService.providerLicensesCreate({
        requestBody: body as unknown as ProviderLicenseWrite,
      }),
    patch: (id, body) =>
      ProviderLicensesService.providerLicensesPartialUpdate({
        id,
        requestBody: body as unknown as PatchedProviderLicenseWrite,
      }),
    destroy: (id) => ProviderLicensesService.providerLicensesDestroy({ id }),
  },
  board: {
    list: (providerId) =>
      ProviderBoardCertificationsService.providerBoardCertificationsList({
        provider: providerId,
      }).then((data) =>
        rowsOf<ProviderBoardCertification>(data as Paginated<ProviderBoardCertification>)
      ),
    create: (body) =>
      ProviderBoardCertificationsService.providerBoardCertificationsCreate({
        requestBody: body as unknown as ProviderBoardCertificationWrite,
      }),
    patch: (id, body) =>
      ProviderBoardCertificationsService.providerBoardCertificationsPartialUpdate({
        id,
        requestBody: body as unknown as PatchedProviderBoardCertificationWrite,
      }),
    destroy: (id) => ProviderBoardCertificationsService.providerBoardCertificationsDestroy({ id }),
  },
  life: {
    list: (providerId) =>
      ProviderCertificationsService.providerCertificationsList({ provider: providerId }).then(
        (data) => rowsOf<ProviderCertification>(data as Paginated<ProviderCertification>)
      ),
    create: (body) =>
      ProviderCertificationsService.providerCertificationsCreate({
        requestBody: body as unknown as ProviderCertificationWrite,
      }),
    patch: (id, body) =>
      ProviderCertificationsService.providerCertificationsPartialUpdate({
        id,
        requestBody: body as unknown as PatchedProviderCertificationWrite,
      }),
    destroy: (id) => ProviderCertificationsService.providerCertificationsDestroy({ id }),
  },
  immunization: {
    list: (providerId) =>
      ProviderImmunizationsService.providerImmunizationsList({ provider: providerId }).then(
        (data) => rowsOf<ProviderImmunization>(data as Paginated<ProviderImmunization>)
      ),
    create: (body) =>
      ProviderImmunizationsService.providerImmunizationsCreate({
        requestBody: body as unknown as ProviderImmunizationWrite,
      }),
    patch: (id, body) =>
      ProviderImmunizationsService.providerImmunizationsPartialUpdate({
        id,
        requestBody: body as unknown as PatchedProviderImmunizationWrite,
      }),
    destroy: (id) => ProviderImmunizationsService.providerImmunizationsDestroy({ id }),
  },
};
